import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { History, RefreshCw, Pencil, UserMinus, Printer, RotateCcw } from "lucide-react";
import api from "../../../services/api";
import { cls, fmtDT, selectCls, inputCls, btnGhost } from "../../ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../components/ui/dialog";
import { SectionHdr } from "../SummaryTab";

const load = () => api.get("/ujms/v2/admin/courtrooms/assignments");
const openPdfPost = async (url, body) => {
  try {
    const r = await api.post(url, body, { responseType: "blob" });
    window.open(URL.createObjectURL(r.data), "_blank");
  } catch (e) { toast.error("Could not generate the welcome sheet"); }
};

/** Administration › Courtroom assignments — seat one presiding judge per courtroom; drives hearing/assignment defaults. */
export function CourtroomAssignments() {
  const [d, setD] = useState(null);
  const [reason, setReason] = useState("");
  const [hist, setHist] = useState(null);
  const refresh = () => load().then((r) => setD(r.data)).catch(() => toast.error("Could not load courtroom assignments"));
  useEffect(() => { refresh(); }, []);
  const assign = async (room, jid) => {
    try {
      const r = await api.post(`/ujms/v2/admin/courtrooms/${room.id}/judge`, { judicial_officer_id: jid || null, reason });
      toast.success(jid ? `${r.data.judge_name} now presides in ${room.name}` : `${room.name} vacated`);
      setReason(""); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Assignment failed"); }
  };
  const showHistory = async (room) => {
    const r = await api.get(`/ujms/v2/admin/courtrooms/${room.id}/judge-history`);
    setHist({ room, rows: r.data });
  };
  if (!d) return <div className="p-2 text-[11px] text-slate-500">Loading…</div>;
  const seated = new Set(d.courtrooms.map((r) => r.judicial_officer_id).filter(Boolean));
  return (
    <div className="grid grid-cols-1 xl:grid-cols-[3fr_1fr] gap-1.5" data-testid="courtroom-assignments">
      <section className="ent-panel">
        <SectionHdr title={`Courtroom assignments (${d.courtrooms.length})`} right={<span className="inline-flex items-center gap-2">
          <input className={cls(inputCls, "w-56 !py-0")} placeholder="Reason for change (audited)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="ca-reason" />
          <button className="underline" onClick={refresh} data-testid="ca-refresh"><RefreshCw className="w-3 h-3 inline" /></button></span>} />
        <table className="w-full border-collapse text-[11px]" data-testid="ca-grid">
          <thead><tr className="text-[10px] text-slate-500 border-b border-slate-200 text-left"><th className="px-1.5">Courtroom</th><th className="px-1.5">Courthouse</th><th className="px-1.5">Presiding judge</th><th className="px-1.5">Since</th><th className="px-1.5">Assigned by</th><th className="px-1.5 text-right">Calendars</th><th className="w-16" /></tr></thead>
          <tbody>
            {d.courtrooms.map((r) => (
              <tr key={r.id} className={cls("border-b border-slate-100 hover:bg-ujms-soft/60", !r.judicial_officer_id && "bg-amber-50/40")} data-testid={`ca-row-${r.id}`}>
                <td className="px-1.5 py-[2px] font-semibold whitespace-nowrap">{r.name}{r.floor ? <span className="text-slate-400 font-normal"> · Fl {r.floor}</span> : null}</td>
                <td className="px-1.5 py-[2px] text-slate-600">{r.courthouse || "—"}</td>
                <td className="px-1.5 py-[2px]">
                  <select className={cls(selectCls, "!py-0 min-w-[220px]")} value={r.judicial_officer_id || ""} onChange={(e) => assign(r, e.target.value)} data-testid={`ca-judge-${r.id}`}>
                    <option value="">— Vacant —</option>
                    {d.judges.map((j) => <option key={j.id} value={j.id}>{j.name}{j.title ? ` (${j.title})` : ""}{seated.has(j.id) && j.id !== r.judicial_officer_id ? ` — sits in ${j.default_courtroom}` : ""}</option>)}
                  </select>
                </td>
                <td className="px-1.5 py-[2px] font-mono whitespace-nowrap text-slate-600">{r.since ? fmtDT(r.since) : "—"}</td>
                <td className="px-1.5 py-[2px] text-slate-600">{r.assigned_by || "—"}</td>
                <td className="px-1.5 py-[2px] text-right tabular-nums">{r.calendars}</td>
                <td className="px-1 py-[2px] text-right"><button className="text-ujms-dark underline inline-flex items-center gap-0.5" onClick={() => showHistory(r)} data-testid={`ca-history-${r.id}`}><History className="w-3 h-3" /> History</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="px-1.5 py-1 text-[10px] text-slate-500">Choosing a judge who already sits elsewhere moves them; the previous courtroom becomes vacant. Assignments default the courtroom on new hearings and case assignments for that judge.</div>
      </section>
      <div className="space-y-1.5">
        <CreateJudgePanel courtrooms={d.courtrooms} onCreated={refresh} />
        <JudgesRoster onChanged={refresh} />
        <section className="ent-panel" data-testid="ca-unassigned">
          <SectionHdr title={`Judges without a courtroom (${d.unassigned_judges.length})`} />
          <ul className="text-[11px] divide-y divide-slate-100">
            {d.unassigned_judges.map((j) => <li key={j.id} className="px-1.5 py-[2px]">{j.name}<span className="text-slate-500"> {j.title ? `· ${j.title}` : ""}</span></li>)}
            {!d.unassigned_judges.length && <li className="px-1.5 py-1 italic text-slate-400">Every judicial officer holds a courtroom.</li>}
          </ul>
        </section>
        {hist && (
          <section className="ent-panel" data-testid="ca-history-panel">
            <SectionHdr title={`${hist.room.name} — seat history`} right={<button className={btnGhost} onClick={() => setHist(null)} data-testid="ca-history-close">Close</button>} />
            <table className="w-full text-[11px]"><tbody>
              {hist.rows.map((h) => <tr key={h.id} className="border-b border-slate-100"><td className="px-1.5 py-[2px] font-semibold">{h.judge}</td><td className="px-1.5 py-[2px] font-mono text-slate-600 whitespace-nowrap">{fmtDT(h.created_at)} → {h.ended_at ? fmtDT(h.ended_at) : "present"}</td><td className="px-1.5 py-[2px] text-slate-600">{h.assigned_by}{h.reason ? ` · ${h.reason}` : ""}</td></tr>)}
              {!hist.rows.length && <tr><td className="px-1.5 py-1 italic text-slate-400">No assignments recorded.</td></tr>}
            </tbody></table>
          </section>
        )}
      </div>
    </div>
  );
}

function CreateJudgePanel({ courtrooms, onCreated }) {
  const [divisions, setDivisions] = useState([]);
  const [f, setF] = useState({ name: "", username: "", password: "", title: "Circuit Judge", division_id: "", courtroom_id: "" });
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  useEffect(() => { api.get("/ujms/v2/admin/divisions").then((r) => setDivisions(r.data)).catch(() => {}); }, []);
  const vacant = (courtrooms || []).filter((r) => !r.judicial_officer_id);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const create = async () => {
    if (!f.name.trim() || !f.username.trim() || f.password.length < 6) { toast.error("Name, username and a 6+ character password are required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/ujms/v2/admin/judges", {
        name: f.name, username: f.username, password: f.password, title: f.title,
        division_id: f.division_id || null, courtroom_id: f.courtroom_id || null,
      });
      toast.success(`${r.data.name} created — signs in as "${r.data.username}"${r.data.courtroom ? `, presiding in ${r.data.courtroom}` : ""}`);
      setLast({ id: r.data.id, username: r.data.username, password: f.password });
      setF({ name: "", username: "", password: "", title: "Circuit Judge", division_id: "", courtroom_id: "" });
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create the judge"); }
    finally { setBusy(false); }
  };
  const printWelcome = () => openPdfPost(`/ujms/v2/admin/judges/${last.id}/welcome-sheet`, { password: last.password, login_url: `${window.location.origin}/login` });
  return (
    <section className="ent-panel" data-testid="create-judge-panel">
      <SectionHdr title="Create a new judge" />
      <div className="p-1.5 space-y-1.5">
        <input className={cls(inputCls, "w-full !py-0.5")} placeholder="Full name (e.g. Hon. Jane Q. Smith)" value={f.name} onChange={set("name")} data-testid="cj-name" />
        <div className="grid grid-cols-2 gap-1.5">
          <input className={cls(inputCls, "!py-0.5")} placeholder="Login username" value={f.username} onChange={set("username")} data-testid="cj-username" />
          <input className={cls(inputCls, "!py-0.5")} type="password" placeholder="Password (6+)" value={f.password} onChange={set("password")} data-testid="cj-password" />
        </div>
        <input className={cls(inputCls, "w-full !py-0.5")} placeholder="Title" value={f.title} onChange={set("title")} data-testid="cj-title" />
        <select className={cls(selectCls, "w-full !py-0.5")} value={f.division_id} onChange={set("division_id")} data-testid="cj-division">
          <option value="">— Division (optional) —</option>
          {divisions.map((dv) => <option key={dv.id} value={dv.id}>{dv.name}</option>)}
        </select>
        <select className={cls(selectCls, "w-full !py-0.5")} value={f.courtroom_id} onChange={set("courtroom_id")} data-testid="cj-courtroom">
          <option value="">— Assign a courtroom now (optional) —</option>
          {vacant.map((r) => <option key={r.id} value={r.id}>{r.name}{r.courthouse ? ` · ${r.courthouse}` : ""}</option>)}
        </select>
        <button className="w-full bg-ujms-dark text-white rounded-sm py-1 text-[11px] font-semibold hover:opacity-90 disabled:opacity-50" onClick={create} disabled={busy} data-testid="cj-submit">{busy ? "Creating…" : "Create judge & login"}</button>
        {last && <button className="w-full border border-ujms-dark text-ujms-dark rounded-sm py-1 text-[11px] font-semibold hover:bg-ujms-soft inline-flex items-center justify-center gap-1" onClick={printWelcome} data-testid="cj-welcome-print"><Printer className="w-3 h-3" /> Print welcome sheet for {last.username}</button>}
        <div className="text-[10px] text-slate-500">Creates a judicial seat plus a sign-in with full Judge permissions. Only vacant courtrooms are listed; reassign occupied ones from the grid.</div>
      </div>
    </section>
  );
}

function JudgesRoster({ onChanged }) {
  const [rows, setRows] = useState(null);
  const [divisions, setDivisions] = useState([]);
  const [edit, setEdit] = useState(null);
  const [retiring, setRetiring] = useState(null);
  const refresh = () => api.get("/ujms/v2/admin/judges", { params: { include_retired: true } }).then((r) => setRows(r.data)).catch(() => toast.error("Could not load judges"));
  useEffect(() => { refresh(); api.get("/ujms/v2/admin/divisions").then((r) => setDivisions(r.data)).catch(() => {}); }, []);
  const reactivate = async (j) => {
    try { const r = await api.post(`/ujms/v2/admin/judges/${j.id}/reactivate`);
      toast.success(`${j.name} reactivated${r.data.login_reactivated ? " — login re-enabled" : ""}. Seat them from the grid.`); refresh(); onChanged && onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not reactivate"); }
  };
  const welcome = (j) => {
    const pwd = window.prompt(`Reset ${j.name}'s password and print a welcome sheet.\n\nEnter a NEW password (6+ chars):`);
    if (!pwd) return;
    if (pwd.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    openPdfPost(`/ujms/v2/admin/judges/${j.id}/welcome-sheet`, { password: pwd, reset: true, login_url: `${window.location.origin}/login` });
    toast.success("Password reset — welcome sheet opened");
  };
  if (!rows) return null;
  const active = rows.filter((j) => !j.retired);
  return (
    <section className="ent-panel" data-testid="judges-roster">
      <SectionHdr title={`Judicial officers (${active.length})`} right={<button className="underline" onClick={refresh} data-testid="jr-refresh"><RefreshCw className="w-3 h-3 inline" /></button>} />
      <table className="w-full text-[11px]"><tbody>
        {rows.map((j) => (
          <tr key={j.id} className={cls("border-b border-slate-100 hover:bg-ujms-soft/60", j.retired && "opacity-60")} data-testid={`jr-row-${j.id}`}>
            <td className="px-1.5 py-[3px]"><div className="font-semibold">{j.name}{j.retired && <span className="ml-1 text-[9px] uppercase font-bold text-red-700 border border-red-300 rounded px-1">Retired</span>}</div>
              <div className="text-[10px] text-slate-500">{j.title}{j.division ? ` · ${j.division}` : ""}{j.retired ? "" : (j.default_courtroom ? ` · ${j.default_courtroom}` : " · no courtroom")}</div>
              <div className="text-[10px] text-slate-400">{j.has_login ? <>login: <span className="font-mono">{j.username}</span>{j.login_active === false ? " (inactive)" : ""}</> : "no login"}</div>
            </td>
            <td className="px-1 py-[3px] text-right whitespace-nowrap">
              {j.retired ? (
                <button className={cls(btnGhost, "!px-1.5 text-emerald-700")} onClick={() => reactivate(j)} data-testid={`jr-reactivate-${j.id}`}><RotateCcw className="w-3 h-3" /> Reactivate</button>
              ) : (<>
                <button className={cls(btnGhost, "!px-1")} title="Edit" onClick={() => setEdit({ ...j })} data-testid={`jr-edit-${j.id}`}><Pencil className="w-3 h-3" /></button>
                <button className={cls(btnGhost, "!px-1")} title="Welcome sheet (reset password)" onClick={() => welcome(j)} data-testid={`jr-welcome-${j.id}`}><Printer className="w-3 h-3" /></button>
                <button className={cls(btnGhost, "!px-1 text-red-700")} title="Retire" onClick={() => setRetiring(j)} data-testid={`jr-retire-${j.id}`}><UserMinus className="w-3 h-3" /></button>
              </>)}
            </td>
          </tr>
        ))}
        {!rows.length && <tr><td className="px-1.5 py-1 italic text-slate-400">No judicial officers.</td></tr>}
      </tbody></table>
      <EditJudgeDialog judge={edit} divisions={divisions} onClose={() => setEdit(null)} onDone={() => { setEdit(null); refresh(); onChanged && onChanged(); }} />
      <RetireJudgeDialog judge={retiring} others={active} onClose={() => setRetiring(null)} onDone={() => { setRetiring(null); refresh(); onChanged && onChanged(); }} />
    </section>
  );
}

function RetireJudgeDialog({ judge, others, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [reassignTo, setReassignTo] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setReason(""); setReassignTo(""); }, [judge]);
  const choices = (others || []).filter((j) => j.id !== judge?.id);
  const submit = async () => {
    if (!reason.trim()) { toast.error("A reason is required"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/ujms/v2/admin/judges/${judge.id}/retire`, { reason, deactivate_login: true, reassign_to: reassignTo || null });
      toast.success(`${judge.name} retired — login disabled${r.data.hearings_reassigned ? `; ${r.data.hearings_reassigned} hearing(s) moved to ${r.data.reassigned_to}` : ""}`);
      onDone();
    } catch (e) {
      const d = e.response?.data?.detail || "Could not retire";
      toast.error(d);
      if (e.response?.status === 409 && !reassignTo && choices.length) toast.message("Select a judge below to receive the upcoming hearings, then retry.");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!judge} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="retire-judge-dialog">
        <DialogHeader><DialogTitle>Retire {judge?.name}</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <div className="text-[11px] text-slate-600">Retiring vacates their courtroom and disables their login. Any upcoming hearings must be moved to another judge.</div>
          <div><label className="text-[11px] font-semibold text-slate-600">Reassign upcoming hearings to</label>
            <select className={cls(selectCls, "w-full")} value={reassignTo} onChange={(e) => setReassignTo(e.target.value)} data-testid="retire-reassign-to">
              <option value="">— No reassignment needed —</option>
              {choices.map((j) => <option key={j.id} value={j.id}>{j.name}{j.default_courtroom ? ` · ${j.default_courtroom}` : ""}</option>)}
            </select>
          </div>
          <div><label className="text-[11px] font-semibold text-slate-600">Reason</label>
            <textarea className={cls(inputCls, "w-full")} rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="retire-reason" /></div>
        </div>
        <DialogFooter>
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className="bg-red-700 text-white rounded-sm px-3 py-1 text-[11px] font-semibold disabled:opacity-50" onClick={submit} disabled={busy} data-testid="retire-submit">Retire judge</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditJudgeDialog({ judge, divisions, onClose, onDone }) {
  const [f, setF] = useState({ name: "", title: "", division_id: "" });
  useEffect(() => { if (judge) setF({ name: judge.name || "", title: judge.title || "", division_id: judge.division_id || "" }); }, [judge]);
  const save = async () => {
    if (!f.name.trim()) { toast.error("Name is required"); return; }
    try { await api.patch(`/ujms/v2/admin/judges/${judge.id}`, { name: f.name, title: f.title, division_id: f.division_id || null });
      toast.success("Judge updated"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };
  return (
    <Dialog open={!!judge} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="edit-judge-dialog">
        <DialogHeader><DialogTitle>Edit judicial officer</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <div><label className="text-[11px] font-semibold text-slate-600">Name</label><input className={cls(inputCls, "w-full")} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="ej-name" /></div>
          <div><label className="text-[11px] font-semibold text-slate-600">Title</label><input className={cls(inputCls, "w-full")} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="ej-title" /></div>
          <div><label className="text-[11px] font-semibold text-slate-600">Division</label>
            <select className={cls(selectCls, "w-full")} value={f.division_id} onChange={(e) => setF({ ...f, division_id: e.target.value })} data-testid="ej-division">
              <option value="">— None —</option>
              {divisions.map((dv) => <option key={dv.id} value={dv.id}>{dv.name}</option>)}
            </select>
          </div>
          <div className="text-[10px] text-slate-500">Renaming also updates the judge's login name and their seated courtroom. Change the courtroom from the grid on the left.</div>
        </div>
        <DialogFooter>
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className="bg-ujms-dark text-white rounded-sm px-3 py-1 text-[11px] font-semibold" onClick={save} data-testid="ej-save">Save changes</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
